import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  closeContactSessionAdmin,
  getContactAdmin,
  listContactSessionMessagesAdmin,
  resetContactAdmin,
  updateContactContextAdmin,
  updateContactOnboardingAdmin,
} from '../../api/contactsAdmin';
import ContactContextSection from '../../components/ContactContextSection';
import ContactOnboardingSection from '../../components/ContactOnboardingSection';
import SessionCard from '../../components/SessionCard';
import { getApiErrorMessage } from '../../utils/errors';

const STATUS_LABELS = {
  active: 'Ativo',
  blocked: 'Bloqueado',
  paused: 'Pausado',
};

function StatusBadge({ status }) {
  const styles = {
    active: 'text-emerald-700 bg-emerald-50',
    blocked: 'text-red-700 bg-red-50',
    paused: 'text-amber-700 bg-amber-50',
  };

  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
        styles[status] || 'text-content-subtle bg-slate-100'
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function AdminContactDetail() {
  const { contactId } = useParams();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetting, setResetting] = useState(false);
  const [savingContext, setSavingContext] = useState(false);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState(() => new Set());
  const [sessionMessages, setSessionMessages] = useState({});
  const [closingSessionId, setClosingSessionId] = useState(null);
  const autoExpandedRef = useRef(false);

  const loadContact = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getContactAdmin(contactId);
      setContact(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o contato.'));
      setContact(null);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    autoExpandedRef.current = false;
    setExpandedSessions(new Set());
    setSessionMessages({});
  }, [contactId]);

  useEffect(() => {
    loadContact();
  }, [loadContact]);

  useEffect(() => {
    if (!contact?.sessions?.length || autoExpandedRef.current) return;

    const activeSession = contact.sessions.find((s) => s.status === 'active');
    if (activeSession) {
      setExpandedSessions(new Set([activeSession.id]));
    }
    autoExpandedRef.current = true;
  }, [contact]);

  const loadSessionMessages = useCallback(
    async (sessionId) => {
      setSessionMessages((prev) => ({
        ...prev,
        [sessionId]: { ...prev[sessionId], loading: true, error: null },
      }));

      try {
        const data = await listContactSessionMessagesAdmin(contactId, sessionId);
        setSessionMessages((prev) => ({
          ...prev,
          [sessionId]: { data: data.data, meta: data.meta, loading: false, error: null },
        }));
      } catch (err) {
        setSessionMessages((prev) => ({
          ...prev,
          [sessionId]: {
            ...prev[sessionId],
            loading: false,
            error: getApiErrorMessage(err, 'Não foi possível carregar as mensagens.'),
          },
        }));
      }
    },
    [contactId]
  );

  const handleToggleSession = useCallback((sessionId) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  }, []);

  const clearSessionState = useCallback(() => {
    setExpandedSessions(new Set());
    setSessionMessages({});
    autoExpandedRef.current = false;
  }, []);

  const handleResetContact = async () => {
    const confirmed = window.confirm(
      `Resetar o contato ${contact.external_id}? Isso apagará onboarding, mensagens, sessões, contexto acumulado e handoff. O número continuará vinculado ao agente.`
    );
    if (!confirmed) return;

    setResetting(true);
    setError('');
    setSuccess('');

    try {
      const data = await resetContactAdmin(contactId);
      setContact(data.contact);
      clearSessionState();
      const { deleted } = data;
      setSuccess(
        `Contato resetado. ${deleted.messages} mensagem(ns), ${deleted.sessions} sessão(ões) removidas.`
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível resetar o contato.'));
    } finally {
      setResetting(false);
    }
  };

  const handleSaveContext = async (progressiveSummary) => {
    setSavingContext(true);
    setError('');
    setSuccess('');

    try {
      const updated = await updateContactContextAdmin(contactId, progressiveSummary);
      setContact(updated);
      setSuccess('Contexto atualizado com sucesso.');
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível atualizar o contexto.'));
      return false;
    } finally {
      setSavingContext(false);
    }
  };

  const handleSaveOnboarding = async ({ onboardingData, onboardingCompleted }) => {
    setSavingOnboarding(true);
    setError('');
    setSuccess('');

    try {
      const updated = await updateContactOnboardingAdmin(contactId, {
        onboardingData,
        onboardingCompleted,
      });
      setContact(updated);
      setSuccess('Onboarding atualizado com sucesso.');
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível atualizar o onboarding.'));
      return false;
    } finally {
      setSavingOnboarding(false);
    }
  };

  const handleCloseSession = async (sessionId) => {
    const confirmed = window.confirm(
      'Fechar esta sessão? A próxima mensagem do contato iniciará uma conversa nova. O resumo será gerado em seguida.'
    );
    if (!confirmed) return;

    setClosingSessionId(sessionId);
    setError('');
    setSuccess('');

    try {
      const updated = await closeContactSessionAdmin(contactId, sessionId);
      setContact(updated);
      setSuccess('Sessão fechada. O resumo será atualizado em breve.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível fechar a sessão.'));
    } finally {
      setClosingSessionId(null);
    }
  };

  if (loading) {
    return (
      <div className="surface-card px-4 py-12 text-center">
        <p className="text-content-muted text-sm">Carregando contato...</p>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="space-y-4">
        <Link to="/admin/contacts" className="text-sm text-forge-600 hover:text-forge-800">
          ← Voltar para contatos
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error || 'Contato não encontrado.'}</p>
        </div>
      </div>
    );
  }

  const metaParts = [
    contact.agent?.name,
    contact.account?.name,
    contact.account?.email,
    contact.onboarding_completed ? 'Onboarding ok' : 'Onboarding pendente',
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Link to="/admin/contacts" className="text-sm text-forge-600 hover:text-forge-800">
          ← Voltar para contatos
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-2xl font-bold text-content-primary">{contact.external_id}</h1>
              <StatusBadge status={contact.status} />
            </div>
            {metaParts.length > 0 && (
              <p className="text-sm text-content-muted mt-1">{metaParts.join(' · ')}</p>
            )}
          </div>

          <button
            type="button"
            onClick={handleResetContact}
            disabled={resetting}
            className="text-sm text-red-600 hover:text-red-700 disabled:opacity-40 shrink-0"
          >
            {resetting ? 'Resetando...' : 'Resetar contato'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-sm text-emerald-700">{success}</p>
        </div>
      )}

      <ContactContextSection
        context={contact.context}
        onSave={handleSaveContext}
        saving={savingContext}
      />

      <ContactOnboardingSection
        onboardingData={contact.onboarding_data}
        onboardingCompleted={contact.onboarding_completed}
        onSave={handleSaveOnboarding}
        saving={savingOnboarding}
      />

      <section className="space-y-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-content-primary">Sessões</h2>
            <p className="text-xs text-content-muted mt-1">
              Histórico de conversas do contato — clique para ver resumo e mensagens
            </p>
          </div>
          <p className="text-sm text-content-subtle">
            {contact.sessions?.length || 0} sessão
            {(contact.sessions?.length || 0) !== 1 ? 'ões' : ''}
          </p>
        </div>

        {contact.sessions?.length > 0 ? (
          <ul className="space-y-2">
            {contact.sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                expanded={expandedSessions.has(session.id)}
                messagesState={sessionMessages[session.id]}
                onToggle={handleToggleSession}
                onLoadMessages={loadSessionMessages}
                onCloseSession={handleCloseSession}
                closing={closingSessionId === session.id}
              />
            ))}
          </ul>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center">
            <p className="text-content-muted text-sm">Nenhuma sessão registrada.</p>
          </div>
        )}
      </section>
    </div>
  );
}

export default AdminContactDetail;
